/**
 * Base composable for all modal selectors
 * Provides common functionality to reduce duplication across selector implementations
 */

import { ref, Ref, watch, shallowRef } from 'vue';
import type { ModalLifecycleEmitFunction } from '../types/emit-types';

/**
 * Configuration for the base selector composable
 */
export interface BaseSelectorConfig<T, TCache = T> {
  /** The prop value - can be entity or ID */
  value: Ref<T | string | T[] | string[] | null | undefined>;

  /** Cache of available items to search when loading by ID */
  cache?: Ref<TCache[]>;

  /** Function to extract ID from an item */
  getItemId?: (item: T) => string;

  /** Function to find item in cache by ID */
  findInCache?: (cache: TCache[], id: string) => T | undefined;

  /** Whether this is a multi-select */
  isMulti?: boolean;

  /** Optional async loader for items not in cache */
  loadById?: (id: string) => Promise<T | null>;

  /**
   * Use isolated mode to prevent reactive contamination.
   * When true, uses shallowRef and manual value copying to prevent
   * reactive connections between parent and child components.
   * This is critical for components that need strict type separation
   * between IDs (strings) and entities (objects).
   */
  isolated?: boolean;
}

/**
 * Base selector return type
 */
export interface BaseSelectorReturn<T> {
  /** Local selected item(s) - always resolved to objects */
  localSelected: Ref<T | null> | Ref<T[]>;

  /** Whether no item is selected */
  hasNoItemSelected: Ref<boolean>;

  /** IDs that need to be loaded */
  idsToLoad: Ref<string | null> | Ref<string[]>;

  /** Loading state */
  isLoading: Ref<boolean>;

  /** Initialize and load items from IDs */
  loadFromIds: () => Promise<void>;

  /** Clear selection */
  clearSelection: () => void;
}

/**
 * Shared composable for base selector functionality
 * Handles ID loading, selection state, and common operations
 */
export function useBaseSelector<T extends Record<string, unknown>, TCache = T>(
  config: BaseSelectorConfig<T, TCache>
): BaseSelectorReturn<T> {
  const {
    value,
    cache,
    findInCache,
    isMulti = false,
    loadById,
    isolated = false,
  } = config;

  // State based on mode - use shallowRef in isolated mode to prevent deep reactivity
  const localSelected = isolated
    ? isMulti
      ? shallowRef<T[]>([])
      : shallowRef<T | null>(null)
    : isMulti
    ? ref<T[]>([])
    : ref<T | null>(null);

  const hasNoItemSelected = ref(false);
  const isLoading = ref(false);

  // IDs to load based on mode
  const idsToLoad = isMulti
    ? (ref<string[]>([]) as Ref<string[]>)
    : (ref<string | null>(null) as Ref<string | null>);

  /**
   * Default cache finder if not provided
   */
  const defaultFindInCache = (cache: TCache[], id: string): T | undefined => {
    // Try to find by common ID fields
    const item = cache.find((item: TCache) => {
      const anyItem = item as Record<string, unknown>;
      return (
        anyItem.id === id || anyItem.userId === id || anyItem.boardId === id
      );
    });
    return item as T | undefined;
  };

  const cacheFinder = findInCache || defaultFindInCache;

  /**
   * Helper to deep clone objects in isolated mode
   */
  const deepClone = <V>(obj: V): V => {
    if (!isolated || !obj || typeof obj !== 'object') return obj;
    return JSON.parse(JSON.stringify(obj));
  };

  // Track if we've done initial sync for multi-mode
  const hasInitializedMulti = ref(false);

  /**
   * Watch value prop and update local state
   *
   * For multi-mode selectors: Only sync on initial mount, then localSelected becomes
   * the source of truth. This prevents parent component reactivity from clearing
   * selections during navigation or other state changes.
   *
   * For single-mode selectors: Continue syncing as the parent controls selection.
   */
  watch(
    () => value.value,
    (newValue) => {
      // For multi-mode: only sync on first run, then ignore prop changes
      // This makes localSelected the source of truth for multi-select
      if (isMulti && hasInitializedMulti.value) {
        return;
      }

      if (!newValue) {
        // Clear selection
        if (isMulti) {
          (localSelected.value as T[]) = [];
          (idsToLoad.value as string[]) = [];
        } else {
          (localSelected.value as T | null) = null;
          (idsToLoad.value as string | null) = null;
        }

        // Mark as initialized for multi-mode
        if (isMulti) {
          hasInitializedMulti.value = true;
        }
        return;
      }

      if (isMulti) {
        // Multi-select mode
        const valueArray = Array.isArray(newValue) ? newValue : [newValue];
        if (valueArray.length > 0 && typeof valueArray[0] === 'string') {
          // Array of IDs
          (idsToLoad.value as string[]) = [...(valueArray as string[])]; // Create new array
          (localSelected.value as T[]) = [];
        } else {
          // Array of objects - deep clone in isolated mode
          const items = valueArray as T[];
          (localSelected.value as T[]) = isolated
            ? items.map(deepClone)
            : items;
          (idsToLoad.value as string[]) = [];
        }

        // Mark as initialized - future prop changes won't override localSelected
        hasInitializedMulti.value = true;
      } else {
        // Single-select mode
        if (typeof newValue === 'string') {
          // Single ID
          (idsToLoad.value as string | null) = newValue;
          (localSelected.value as T | null) = null;
        } else if (Array.isArray(newValue)) {
          // Unexpected array in single mode - take first item
          const first = newValue[0];
          if (first) {
            if (typeof first === 'string') {
              (idsToLoad.value as string | null) = first;
              (localSelected.value as T | null) = null;
            } else {
              // Deep clone in isolated mode
              (localSelected.value as T | null) = isolated
                ? deepClone(first as T)
                : (first as T);
              (idsToLoad.value as string | null) = null;
            }
          }
        } else {
          // Single object - deep clone in isolated mode
          (localSelected.value as T | null) = isolated
            ? deepClone(newValue as T)
            : (newValue as T);
          (idsToLoad.value as string | null) = null;
        }
      }
    },
    { immediate: true }
  );

  /**
   * Load items from IDs using cache or async loader
   */
  const loadFromIds = async (): Promise<void> => {
    const ids = idsToLoad.value;

    // Skip if no IDs to load
    if (!ids || (Array.isArray(ids) && ids.length === 0)) {
      return;
    }

    // Skip if already have selected items
    if (isMulti && (localSelected.value as T[]).length > 0) {
      return;
    }
    if (!isMulti && localSelected.value) {
      return;
    }

    try {
      isLoading.value = true;

      if (isMulti) {
        // Load multiple items
        const idsArray = ids as string[];
        const loadedItems: T[] = [];

        for (const id of idsArray) {
          let item: T | undefined = undefined;

          // Try cache first
          if (cache?.value) {
            item = cacheFinder(cache.value, id);
          }

          // Try async loader if not in cache
          if (!item && loadById) {
            const loaded = await loadById(id);
            if (loaded) {
              item = loaded;
            }
          }

          if (item) {
            loadedItems.push(isolated ? deepClone(item) : item);
          }
        }

        (localSelected.value as T[]) = loadedItems;
      } else {
        // Load single item
        const id = ids as string;
        let item: T | undefined = undefined;

        // Try cache first
        if (cache?.value) {
          item = cacheFinder(cache.value, id);
        }

        // Try async loader if not in cache
        if (!item && loadById) {
          const loaded = await loadById(id);
          if (loaded) {
            item = loaded;
          }
        }

        (localSelected.value as T | null) = item
          ? isolated
            ? deepClone(item)
            : item
          : null;
      }
    } catch (error) {
      // Error during item loading - already handled by returning null
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clear selection
   */
  const clearSelection = (): void => {
    if (isMulti) {
      (localSelected.value as T[]) = [];
    } else {
      (localSelected.value as T | null) = null;
    }
    hasNoItemSelected.value = false;
  };

  return {
    localSelected: localSelected as Ref<T | null> | Ref<T[]>,
    hasNoItemSelected,
    idsToLoad,
    isLoading,
    loadFromIds,
    clearSelection,
  };
}

/**
 * Composable for handling modal lifecycle events
 */
export interface ModalLifecycleConfig {
  /** Function to call on show */
  onShow?: () => void | Promise<void>;

  /** Function to call on hide */
  onHide?: () => void | Promise<void>;

  /** Function to call on shown (after animation) */
  onShown?: () => void | Promise<void>;

  /** Event emitter */
  emit?: ModalLifecycleEmitFunction;
}

/**
 * Shared modal lifecycle handling
 */
export function useModalLifecycle(config: ModalLifecycleConfig) {
  const {
    onShow: customOnShow,
    onHide: customOnHide,
    onShown: customOnShown,
    emit,
  } = config;

  const onShow = async () => {
    if (customOnShow) {
      await customOnShow();
    }
    emit?.('show');
  };

  const onHide = async () => {
    if (customOnHide) {
      await customOnHide();
    }
    emit?.('hide');
  };

  const onShown = async () => {
    if (customOnShown) {
      await customOnShown();
    }
  };

  return {
    onShow,
    onHide,
    onShown,
  };
}

/**
 * Composable for handling save/cancel operations
 */
export interface SaveHandlerConfig<T> {
  /** Current selected value(s) */
  localSelected: Ref<T | null> | Ref<T[]>;

  /** Whether "no item" is selected */
  hasNoItemSelected: Ref<boolean>;

  /** Whether to close on select */
  closeOnSelect?: Ref<boolean>;

  /** Function to close the modal */
  closeModal: () => void;

  /** Event emitter - accepting flexible emit function */
  emit: (event: string, ...args: unknown[]) => void;

  /** Whether this is multi-select */
  isMulti?: boolean;

  /** Optional function to get focused item for single select */
  getFocusedItem?: () => T | 'no-item' | null;
}

/**
 * Shared save/cancel handling
 */
export function useSaveHandler<T>(config: SaveHandlerConfig<T>) {
  const {
    localSelected,
    hasNoItemSelected,
    closeOnSelect,
    closeModal,
    emit,
    isMulti = false,
    getFocusedItem,
  } = config;

  const handleSave = () => {
    if (!isMulti && getFocusedItem) {
      // Single select mode - check for focused item first
      const selected = localSelected.value as T | null;
      const hasNoItem = hasNoItemSelected.value;

      if (!selected && !hasNoItem) {
        // No selection yet - try to use focused item
        const focused = getFocusedItem();
        if (focused === 'no-item') {
          emit('update:value', null);
          emit('no-item-selected');
          closeModal();
          return;
        } else if (focused) {
          emit('update:value', focused);
          emit('selected', focused);
          closeModal();
          return;
        }
        // No focused item - just close
        closeModal();
        return;
      }
    }

    // Standard save logic
    const value = localSelected.value;
    if (isMulti) {
      emit('update:value', value);
      emit('selected', value);
    } else {
      if (value) {
        emit('update:value', value);
        emit('selected', value);
      } else if (hasNoItemSelected.value) {
        emit('update:value', null);
        emit('no-item-selected');
      }
    }

    closeModal();
  };

  const handleCancel = () => {
    closeModal();
  };

  const handleItemSelected = (item: T) => {
    if (isMulti) {
      // Multi-select doesn't auto-close
      return;
    }

    // Single select - update and potentially close
    (localSelected.value as T | null) = item;
    hasNoItemSelected.value = false;

    emit('update:value', item);
    emit('selected', item);

    if (closeOnSelect?.value) {
      closeModal();
    }
  };

  const handleNoItemSelected = () => {
    if (isMulti) {
      // Multi-select doesn't use no-item
      return;
    }

    (localSelected.value as T | null) = null;
    hasNoItemSelected.value = true;

    emit('update:value', null);
    emit('no-item-selected');

    if (closeOnSelect?.value) {
      closeModal();
    }
  };

  return {
    handleSave,
    handleCancel,
    handleItemSelected,
    handleNoItemSelected,
  };
}
